import { Inject, Injectable } from '@angular/core';
import { from, Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { Environment } from '../../../app/types/environment.interface';
import { AddHeadersRequestPolicy } from '../../addresses-store/util';
import { ENVIRONMENT } from '../../core';
import {
  ApplicantImageResponse,
  ApplicantImageUploadOperationResponse,
  Basket,
  BasketCheckoutOperationResponse,
  BasketCheckoutRequest,
  BasketItemRequest,
  CheckoutAPI,
  CheckoutAPIOptionalParams,
  DDAccountVerifyRequest,
  DDAccountVerifyResponse,
  PostcodeTicketVerifyRequest,
  PostcodeTicketVerifyResponse,
} from '../client/src';
import { BasketDeleted } from '../types';
import { ImageUpload } from '../types/image-upload.interface';

@Injectable({
  providedIn: 'root',
})
export class CheckoutService {
  protected readonly baseApi: CheckoutAPI;

  constructor(@Inject(ENVIRONMENT) private env: Environment) {
    const clientParams: CheckoutAPIOptionalParams = {
      baseUri: `${env.nxApi.base}/${env.nxApi.fragment.checkout}`,
      endpoint: `${env.nxApi.base}/${env.nxApi.fragment.checkout}`,
      additionalPolicies: [
        { policy: new AddHeadersRequestPolicy(), position: 'perCall' },
      ],
      retryOptions: {
        maxRetries: 0,
      },
    };

    this.baseApi = new CheckoutAPI(clientParams);
  }

  public loadBasketById(basketId: string): Observable<Basket> {
    return from(this.baseApi.getBasketByID(basketId));
  }

  public createBasket(item: BasketItemRequest): Observable<Basket> {
    return from(
      this.baseApi.basketCreate({
        body: {
          ...item,
        },
      })
    );
  }

  public deleteBasket(basketId: string): Observable<BasketDeleted> {
    return from(
      this.baseApi.deleteBasketByID(basketId, {
        requestOptions: {
          customHeaders: {
            'Content-Type': 'application/json',
          },
        },
      })
    ).pipe(
      switchMap(() => {
        return of({
          isDeleted: true,
        });
      })
    );
  }

  public addItemToBasket(
    basketId: string,
    item: BasketItemRequest
  ): Observable<Basket> {
    return from(
      this.baseApi.basketAddItem(basketId, {
        body: {
          ...item,
        },
      })
    );
  }

  public updateItemInBasket(
    basketId: string,
    item: BasketItemRequest
  ): Observable<Basket> {
    return from(
      this.baseApi.basketUpdateItem(basketId, item.ticketID, {
        body: {
          ...item,
        },
      })
    );
  }

  public removeItemFromBasket(
    basketId: string,
    itemId: number
  ): Observable<Basket> {
    return from(
      this.baseApi.basketDeleteItem(basketId, itemId, {
        requestOptions: {
          customHeaders: {
            'Content-Type': 'application/json',
          },
        },
      })
    );
  }

  public validateDirectDebit(
    accountNumber: string,
    sortCode: string
  ): Observable<DDAccountVerifyResponse> {
    const request: DDAccountVerifyRequest = {
      accountNumber: window.btoa(accountNumber),
      sortCode: window.btoa(sortCode),
    };
    return from(
      this.baseApi.directDebitAccountVerify({
        body: request,
      })
    );
  }

  public applicantImageUpload(
    image: ImageUpload
  ): Observable<ApplicantImageUploadOperationResponse> {
    const formData = new FormData();
    formData.append('image', image.image);

    return from(
      this.baseApi.applicantImageUpload(image.basketId, image.itemId, image.quantityIndex, {
        image: formData.get('image'),
      })
    );
  }

  public applicantImageLoad(
    basketId: string,
    itemId: string,
    quantityIndex: number
  ): Observable<Blob | undefined> {
    return from(
      this.baseApi.applicantImage(basketId, itemId, quantityIndex).then((response) => {
        return response.blobBody;
      })
    );
  }

  public postcodeVerify(
    request: PostcodeTicketVerifyRequest
  ): Observable<PostcodeTicketVerifyResponse> {
    return from(
      this.baseApi.postcodeTicketVerify({
        body: request,
      })
    );
  }

  public basketCheckout({
    token,
    basketId,
    basketRequest,
  }: {
    token: string;
    basketId: string;
    basketRequest: BasketCheckoutRequest;
  }): Observable<BasketCheckoutOperationResponse> {
    return from(
      this.baseApi.basketCheckout(basketId, token, {
        body: basketRequest,
      })
    );
  }
}
